import React from 'react';
import { Link } from 'react-router-dom';
import { afterEndWords, aOrAn, clock12, clockWords, lateWords, newTimeText, promisedWords, shortTime, startPlace, telHref, waitWords, windowWords, ClockTone } from '../utils/dayPlan';
import { textsPath } from '../utils/textsLink';
import { DEFAULT_COMPANY } from '../utils/company';

/**
 * The plain-words half of a planned day — the Field page's "Best order" panel,
 * its stop cards, and the calendar's route preview all read from here, so the
 * same stop never says two different things on two screens.
 *
 * Owner, Oct 5 2026, on his Field page: "I want the optimize and re route
 * button to actually work — mario has in between 7-10 but stuck at 7". The
 * plan showed a visit set BETWEEN 7 and 10 as "⏰ 7:00 AM · 176 min late",
 * three kept promises late behind it, and one red sentence listing five
 * names. So each stop now says when he will really be there (a window as a
 * window), and each problem gets its own short paragraph with the reason and
 * the one thing to do about it — on his phone, in the truck.
 */

// Red is for a time that will be MISSED and nothing else. A kept time used to
// be red too (a must-start row always was), which was fine while a late row
// also said "24 min late" — once that became "· there ~4:24 PM", a kept
// "⏰ 7–10 AM · there ~8:26 AM" and a late "⏰ 4:00 PM · there ~4:24 PM" were
// the same red line under a green "All set times kept".
// Amber "behind" is a kept time a few minutes off (the server's rule,
// dayRoute.LATE_OK): worth a glance, not a phone call.
const TONE: Record<ClockTone, string> = {
  kept: 'text-gray-900 font-semibold',
  behind: 'text-amber-700 font-semibold',
  late: 'text-red-600 font-semibold',
  moved: 'text-amber-700 font-semibold',
  anytime: 'text-gray-500',
};

/** The time on one row of the plan (or on a stop card). */
export function PlanRowTime({ p }: { p: any }) {
  // A stop the plan does not move: finished, or under way. (A skipped stop is
  // out of the day and is not sent here at all — but if one ever is, it says
  // "skipped": it used to read "started", for a mow he had called off.)
  if (p.fixed) return <span className="text-xs whitespace-nowrap text-gray-500">{p.status === 'complete' ? 'done' : p.status === 'skipped' ? 'skipped' : 'started'}</span>;
  const w = clockWords(p);
  return <span className={`text-xs whitespace-nowrap ${TONE[w.tone]}`}>{w.text}</span>;
}
export const clockToneClass = (tone: ClockTone) => TONE[tone];

/**
 * " (promised 10:00 AM)" after a name on a list that already leads with the
 * arrival time — the calendar's route preview. Plain when the time is kept,
 * red with the minutes when it runs late, amber when it can't be kept at all.
 * In the list's own text size.
 */
export function PlanPromised({ p }: { p: { must_start?: string | null; late_minutes?: number | null; behind_minutes?: number | null; gone_by?: boolean; passed?: boolean; moved?: boolean } }) {
  const w = promisedWords(p);
  if (!w) return null;
  return <span className={w.tone === 'kept' ? 'text-gray-500' : TONE[w.tone]}> ({w.text})</span>;
}

/** "All 8 set times kept" / "6 of 8 set times kept". Nothing when the day has no set times. */
export function PlanKept({ plan }: { plan: any }) {
  const n = Number(plan?.set_times) || 0, k = Number(plan?.kept) || 0;
  if (!n) return null;
  return k >= n
    ? <p className="text-xs font-semibold text-green-700">{n === 1 ? 'The set time is kept' : `All ${n} set times kept`}</p>
    : <p className="text-xs font-semibold text-amber-700">{k} of {n} set time{n === 1 ? '' : 's'} kept</p>;
}

/** How the planner treats times — one sentence under the plan. */
export function PlanHelp() {
  return (
    <p className="text-[11px] text-gray-500">
      ⏰ stops keep their time, a "between" visit can land anywhere in its window, and anytime stops fill the gaps. A time that has already gone by can't be kept, so it is fitted into the first gap that doesn't make anyone else late. Pick a later start to leave the morning free. Times aren't written on the jobs — anytime stays anytime. After saving you can still drag stops by the ⠿ handle.
    </p>
  );
}

/** "You wait about 44 min before Carla Mendez." — nothing when the plan has no long sit in it. */
export function PlanWaits({ plan }: { plan: any }) {
  const words = waitWords(plan);
  return words ? <p className="text-xs text-gray-500">{words}</p> : null;
}

const linkCls = 'underline font-semibold';
const amberBox = 'text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-2';
const redBox = 'text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-2.5 py-2';

/**
 * The stops a plan only reaches after his day ends — "3 stops land after 9 PM
 * and won't happen today: …". Owner, Oct 5 2026: "I want the optimize and re
 * route button to actually work". A plan made at 12:50 PM ran to 11:26 PM —
 * a mow at 7:46 PM, a quote visit at 10:46 PM — without a word. The page does
 * not move them (which day they go to is his call); it only stops pretending
 * they will happen. The hour is his setting (Settings → Your day ends at).
 */
export function PlanAfterEnd({ plan, isToday }: { plan: any; isToday: boolean }) {
  const words = afterEndWords(plan, isToday);
  if (!words) return null;
  // "or start earlier" only when he could: a plan that already leaves now can't.
  return <p className={amberBox}>{words} Nothing has been moved — move them to another day{plan?.leave_now ? '' : ', or start earlier'}.</p>;
}

/**
 * One paragraph per set time that can't be kept. Each says only what is true:
 *   gone by         — the real clock is past it (today). Fitted into a gap;
 *                     call with the new time, or mark it done.
 *   before the start — he picked a later start (or it is another day): the
 *                     time has not gone by, it is just ahead of when the day
 *                     begins. Call, or start earlier. (At 8:55 AM, with Start
 *                     at 10:30, the page used to say Mario's 7–10 window "has
 *                     already gone by".)
 *   no time left    — not gone by, but too late even driving straight there.
 *   moved           — could be reached, but only by making others late.
 *   kept, late      — why (the stop before it, and the drive), and text the
 *                     new time. A window he reaches inside its hours but
 *                     can't finish in says exactly that — "there about 9:56"
 *                     is not "late" to a customer told 7–10.
 * "call" and "text" open his own phone; nothing is sent from the server.
 *
 * `saved`: these are the problems of the order he SAVED, shown above his list
 * all day (they used to exist only inside the Optimize preview — once he
 * tapped "Use this order" the call and text links were gone). Nothing was
 * "fitted in" there by a planner: the stop is where he put it, so it says "in
 * your order you get there about …".
 */
export function PlanProblems({ plan, isToday, dayWords, me, saved }: { plan: any; isToday: boolean; dayWords: string; me: string; saved?: boolean }) {
  const moved: any[] = plan?.moved || [], late: any[] = plan?.late || [];
  if (!moved.length && !late.length) return null;
  const open: any[] = (plan?.stops || []).filter((s: any) => !s.fixed);
  const before = (id: number) => { const i = open.findIndex(s => s.job_id === id); return i > 0 ? open[i - 1] : null; };
  // What they were told: "10:00 AM", or a window — "7–10 AM", "by 10 AM".
  const win = (e: any) => (e.must_start ? '' : windowWords(e.window));
  const bothEnds = (e: any) => !e.must_start && !!e.window?.earliest && !!e.window?.latest;
  const told = (e: any) => (e.must_start ? clock12(e.must_start) : win(e));
  // "in the 7–10 AM window" · "by 10:00 AM" · "by 10 AM"
  const inTime = (e: any) => (bothEnds(e) ? `in the ${told(e)} window` : e.must_start ? `by ${told(e)}` : told(e));
  const body = (e: any, behind: boolean) => newTimeText({
    customer: e.customer_name, me, company: DEFAULT_COMPANY.name, eta: e.eta, isToday, dayWords, behind,
    was: e.must_start ? clock12(e.must_start) : bothEnds(e) ? `between ${win(e)}` : win(e) || null,
  });
  const call = (e: any, word: string) => (e.phone ? <a href={telHref(e.phone)} className={linkCls}>{word}</a> : <>{word}</>);
  // The words land in their thread on Texts (?body=), ready to send from the business number.
  const text = (e: any, word: string, behind = true) => (e.phone ? <Link to={textsPath(e.phone, body(e, behind))} className={linkCls}>{word}</Link> : <>{word}</>);
  // "call or text Mario" — a customer who opted out of texts gets a call only; a to-do has nobody to tell.
  const reach = (e: any, behind = true) => (e.no_text
    ? <>{call(e, 'call')} {e.customer_name}</>
    : <>{call(e, 'call')} or {text(e, 'text', behind)} {e.customer_name}</>);
  const dayStart = clock12(plan?.asked_start || plan?.start);
  // Where the plan leaves from, by name: "home", "Mario Delgado's". It used to
  // read "from where the day starts", which at 1 PM in a customer's yard meant
  // nothing to him. And when the plan already leaves NOW, "or start earlier"
  // is not something he can do — so it is not offered.
  const place = startPlace(plan) || 'where the day starts';
  const canStartEarlier = !plan?.leave_now;
  // Where the new time comes from: the planner found it a gap, or it is simply where it sits in his own order.
  const fitted = (at: string) => (saved ? <>In your order you get there about {at}</> : <>It's fitted in at about {at}</>);
  // A stop that turned up after he saved his order (a customer booked online,
  // the office added a visit) is not "10½ hours late — text her the new time":
  // it is simply not in the order yet and sits at the end of the list. Say that,
  // and that Re-plan (the button just above) is what fits it in.
  const rowOf = (id: number) => (plan?.stops || []).find((x: any) => x.job_id === id);
  const added = (e: any) => !!saved && rowOf(e.job_id)?.in_order === false;
  const addedNote = (e: any, key: string) => (
    <p key={key} className={amberBox}>
      {e.service_type === 'todo' ? <>"{e.customer_name}"</> : <>{e.customer_name}</>} ({told(e)}) was added after you set this order, so it sits last — about {clock12(e.eta)}. Re-plan to fit it in.
    </p>
  );
  return (
    <div className="space-y-1.5">
      {moved.map(e => {
        const todo = e.service_type === 'todo';
        const at = clock12(e.eta);
        const key = `m${e.job_id}`;
        if (added(e)) return addedNote(e, key);
        const beforeNoon = Number(String(e.must_start || e.window?.latest || e.window?.earliest || '').slice(0, 2)) < 12;
        const what = todo ? <>"{e.customer_name}" ({told(e)})</>
          : e.must_start ? <>{e.customer_name}'s {told(e)}</>
            : bothEnds(e) ? <>{e.customer_name}'s {told(e)} window</>
              : <>{e.customer_name}'s visit ({told(e)})</>;
        const holds = beforeNoon ? 'the morning' : 'its place';
        // The real clock is past it.
        if (e.gone_by) {
          return (
            <p key={key} className={amberBox}>
              {what} has already gone by{saved ? '.' : <>, so it no longer holds {holds}.</>} {fitted(at)}
              {todo ? <> — or mark it done if you already did it.</> : <> — {reach(e)} with the new time, or mark the visit done if you already went.</>}
            </p>
          );
        }
        // Not gone by — just ahead of the start he picked (or of another day's start).
        if (e.passed) {
          return (
            <p key={key} className={amberBox}>
              {what} is before the day starts ({dayStart}){saved ? '.' : <>, so it no longer holds {holds}.</>} {fitted(at)}
              {todo ? <> — or start the day earlier.</> : <> — {reach(e, false)} with the new time, or start the day earlier.</>}
            </p>
          );
        }
        // Still ahead of him, but too close to make even driving straight there.
        if (e.out_of_reach) {
          return (
            <p key={key} className={amberBox}>
              There isn't time left to {todo ? <>get to "{e.customer_name}"</> : <>reach {e.customer_name}</>} {inTime(e)} from a {dayStart} start.
              {' '}Fitted in at about {at}{todo ? (isToday ? '.' : ' — or start the day earlier.') : <> — {reach(e)} with the new time{isToday ? '' : ', or start the day earlier'}.</>}
            </p>
          );
        }
        // Its time is still ahead of him — set a minute ago, maybe — so the
        // text is a change of time ("I can be there about 4:26 PM instead of
        // 11:15 AM"), not "I'm running behind today".
        return (
          <p key={key} className={amberBox}>
            You can't {todo ? <>get to "{e.customer_name}"</> : <>reach {e.customer_name}</>} {inTime(e)} without making other set times late.
            {' '}Fitted in at about {at}{todo ? '.' : <> — {reach(e, false)}.</>}
          </p>
        );
      })}
      {late.map(e => {
        if (added(e)) return addedNote(e, `l${e.job_id}`);
        const todo = e.service_type === 'todo';
        const prev = before(e.job_id);
        const drive = Number(e.drive_minutes) || 0;
        const name = todo ? <>"{e.customer_name}"</> : <>{e.customer_name}</>;
        const why = e.after
          ? <>{prev?.service_type === 'todo' ? <>"{e.after}" runs</> : <>{e.after}'s visit runs</>} to {clock12(e.after_depart)}{drive > 0 ? <> and {todo ? 'it' : e.customer_name} is {aOrAn(drive)} {drive}-minute drive away</> : null}.</>
          // The first stop of the day. With no drive to blame (a to-do with no
          // address) the reason is the start itself — it used to read "it is a
          // 0-minute drive from where the day starts".
          : drive > 0 ? <>it is {aOrAn(drive)} {drive}-minute drive from {place}.</> : <>the day doesn't start until {clock12(plan?.start)}.</>;
        const orElse = e.after ? ', or move one of them' : canStartEarlier ? ', or start earlier' : '';
        const fix = todo
          ? (e.after ? 'Move one of them, or change the time.' : canStartEarlier ? 'Start earlier, or change the time.' : 'Change the time.')
          : e.no_text
            ? <>{call(e, 'Call')} {e.customer_name} with the new time{orElse}.</>
            : <>{text(e, 'Text')} {e.customer_name} the new time{orElse}.</>;
        // A window he reaches while it is still open, but the visit runs past
        // its end: the arrival is not late, the finish is.
        // late_told: a window he reaches after it has closed is late from its last minute, the way the customer counts it.
        const insideWindow = !e.must_start && e.window?.latest && e.eta && e.depart && String(e.eta) <= String(e.window.latest);
        return (
          <p key={`l${e.job_id}`} className={redBox}>
            {insideWindow
              ? <>{name}'s visit ({told(e)}) won't be finished by {shortTime(e.window.latest)} — there about {clock12(e.eta)}, done about {clock12(e.depart)}: {why}</>
              : <>{name} ({told(e)}) will be about {lateWords(e.late_told ?? e.late_minutes)} late: {why}</>}
            {' '}{fix}
          </p>
        );
      })}
    </div>
  );
}
